import { destroy, getWithConfig, postCustomize, put } from "../config/api";
import { getClient, getToken, getUid } from "./AuthService";

export type UserStatusFilter = "active" | "inactive" | "all";

export interface AdminRole {
  id: number;
  name: string;
  activities: string[];
}

export interface AdminUser {
  id: number;
  name: string | null;
  email: string;
  roles: AdminRole[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminUsersList {
  users: AdminUser[];
  total: number;
}

export interface AdminUsersQuery {
  q?: string;
  status: UserStatusFilter;
  roleId?: number;
}

export interface AdminUserForm {
  name: string;
  email: string;
  roleIds: number[];
  password?: string;
  passwordConfirmation?: string;
}

export type AdminUserErrors = { [field: string]: string[] | undefined };

const authHeaders = () => ({
  "Access-Token": getToken(),
  client: getClient(),
  Uid: getUid(),
});

export function createAdminUsersService(basePath: string, rolesPath: string) {
  return {
    async list(query: AdminUsersQuery) {
      return getWithConfig<AdminUsersList>(basePath, { headers: authHeaders(), params: query })
        .then((response) => response.data);
    },

    async create(form: AdminUserForm) {
      return postCustomize<AdminUser>(basePath, form, { headers: authHeaders() })
        .then((response) => response.data);
    },

    async update(id: number, form: AdminUserForm) {
      return put<AdminUser>(`${basePath}/${id}`, form, { headers: authHeaders() })
        .then((response) => response.data);
    },

    async deactivate(id: number) {
      return destroy<AdminUser>(`${basePath}/${id}`, { headers: authHeaders() })
        .then((response) => response.data);
    },

    async reactivate(id: number) {
      return postCustomize<AdminUser>(`${basePath}/${id}/reativacao`, {}, { headers: authHeaders() })
        .then((response) => response.data);
    },

    async roles() {
      return getWithConfig<AdminRole[]>(rolesPath, { headers: authHeaders() })
        .then((response) => response.data);
    },
  };
}

export type AdminUsersApi = ReturnType<typeof createAdminUsersService>;

export default createAdminUsersService("/admin/usuarios", "/admin/perfis");

export function userErrors(error: unknown): AdminUserErrors | null {
  const errors = typeof error === "object" && error !== null ? (error as { errors?: unknown }).errors : undefined;
  return typeof errors === "object" && errors !== null && !Array.isArray(errors) ? (errors as AdminUserErrors) : null;
}
